import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './auth';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { Login } from './pages/Login';
import { Applications } from './pages/Applications';
import { ApplicationDetail } from './pages/ApplicationDetail';
import { Superusers } from './pages/Superusers';
import { Users } from './pages/Users';
import { Roles } from './pages/Roles';
import { Permissions } from './pages/Permissions';
import { Account } from './pages/Account';

/**
 * Route guard. These gates are convenience only — every endpoint re-checks
 * authorisation server-side, so a tampered client gains nothing.
 */
function Require({ kind, children }: { kind: 'superuser' | 'user'; children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner label="Checking your session…" />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (session.kind !== kind) {
    return <Navigate to={session.kind === 'superuser' ? '/admin' : '/app'} replace />;
  }
  return <>{children}</>;
}

function Home() {
  const { session, loading } = useAuth();
  if (loading) return <Spinner label="Checking your session…" />;
  if (!session) return <Navigate to="/login" replace />;
  return <Navigate to={session.kind === 'superuser' ? '/admin' : '/app'} replace />;
}

const SUPERADMIN_NAV = [
  { to: '/admin', label: 'Applications', end: true },
  { to: '/admin/superusers', label: 'Superadmins' },
];

const TENANT_NAV = [
  { to: '/app', label: 'Users', end: true },
  { to: '/app/roles', label: 'Roles' },
  { to: '/app/permissions', label: 'Permissions' },
  { to: '/app/account', label: 'Account' },
];

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />

      <Route
        path="/admin"
        element={
          <Require kind="superuser">
            <Layout nav={SUPERADMIN_NAV} />
          </Require>
        }
      >
        <Route index element={<Applications />} />
        <Route path="applications/:applicationId" element={<ApplicationDetail />} />
        <Route path="superusers" element={<Superusers />} />
      </Route>

      <Route
        path="/app"
        element={
          <Require kind="user">
            <Layout nav={TENANT_NAV} />
          </Require>
        }
      >
        <Route index element={<Users />} />
        <Route path="roles" element={<Roles />} />
        <Route path="permissions" element={<Permissions />} />
        <Route path="account" element={<Account />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
